import { useRef, useState } from "react";
import { QueryClientProvider, QueryClient } from "react-query";
import { CommonUserTaskNew } from "../CommonUserTaskNew";

import type {
  RefinanceFormData,
  ExtendedRefinancingDetails,
  RefinancingDetails,
} from "./types";
import type { ComponentConfig } from "../types";
import type { TaskProps } from "../../../types/TaskProps";
import { LoanDetailsInfo } from "./loanDetails";
import { RefinanceDetailsTab } from "./refinanceDetails";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      suspense: false,
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      useErrorBoundary: true,
      staleTime: 1000 * 10,
    },
  },
});

const UpdateRefinance = (props: TaskProps) => {
  const { task, trigger, save } = props;
  const country = props.flow?.data?.country;

  const noErrorMessage = "";
  const [errorMessages, setErrorMessages] = useState<string>(noErrorMessage);

  const [comment, setComment] = useState<string>(task?.data?.comment ?? "");

  const savedRefinanceDetails: ExtendedRefinancingDetails[] = task?.data?.refinancingDetails;
  // The Id of the last refinance detail that where saved
  const lastRefinanceDetailsId = savedRefinanceDetails ? Number.parseInt(savedRefinanceDetails[savedRefinanceDetails.length - 1].id) : 0;

  const refinanceDetailsId = useRef(lastRefinanceDetailsId);
  const { refinanceDetails, applicantInfo, offer, internalRedemption } = task.context;

  let extendedRefinancingDetails: ExtendedRefinancingDetails[] = [];
  if (!savedRefinanceDetails) {
    extendedRefinancingDetails = refinanceDetails?.map(
      (details: ExtendedRefinancingDetails, index: number) => {
        refinanceDetailsId.current++;
        return {
          ...details,
          order: refinanceDetailsId.current,
          id: refinanceDetailsId.current.toString(),
        };
      },
    );
  }

  const [newRefinancingDetails, setNewRefinancingDetails] = useState<ExtendedRefinancingDetails[]>(savedRefinanceDetails ?? extendedRefinancingDetails);

  let totalRefinanceAmount = Number.parseFloat(
    newRefinancingDetails?.reduce((sum, detail) => {
      if (Number.isNaN(detail.amount)) {
        detail.amount = 0;
      }
      return sum + (typeof detail.amount === "string" ? 0 : detail.amount);
    }, 0).toFixed(2),
  );

  if (internalRedemption) {
    totalRefinanceAmount += Number.parseFloat(Number(internalRedemption.amount).toFixed(2));
  }

  const loanDetails = {
    bankAccount: applicantInfo?.bankAccountNumber,
    finalLoanAmount: offer?.finalLoanAmount,
    totalRefinanceAmount: totalRefinanceAmount,
  };
  let amountSum = 0;

  let formData: RefinanceFormData = {
    refinancingDetails: newRefinancingDetails?.map(
      (detail: RefinancingDetails) => {
        const { ...visibleDetails } = detail;
        return visibleDetails;
      },
    ),
    comment: comment,
  };

  const onComplete = async () => {
    if (errorMessages !== noErrorMessage) {
      return;
    }
    if (offer.finalLoanAmount - amountSum < 0) {
      setErrorMessages(
        "The refinance amount cannot be higher than total loan amount.",
      );
      return;
    }

    formData = {
      refinancingDetails: newRefinancingDetails?.map(
        (detail: RefinancingDetails) => {
          const { ...visibleDetails } = detail;
          if (visibleDetails.amount === "") {
            visibleDetails.amount = 0;
          }

          if (visibleDetails.type !== "loan" && visibleDetails.type !== "card") {
            visibleDetails.type = "loan";
          }

          if (visibleDetails.monthlyAmount && typeof visibleDetails.monthlyAmount === "string")
            visibleDetails.monthlyAmount = Number.parseFloat(visibleDetails?.monthlyAmount ?? "0");


          if (typeof visibleDetails.isFromUc === "string") {
            visibleDetails.isFromUc = visibleDetails.isFromUc === "true";
          }

          return visibleDetails;
        },
      ),
      comment: comment,
    };

    trigger(
      formData,
      () => { },
      (e: any) => {
        setErrorMessages(e?.detail);
      },
    );
  };

  const onSave = async () => {
    formData = {
      refinancingDetails: newRefinancingDetails?.map(
        (detail: RefinancingDetails) => {
          const { ...visibleDetails } = detail;
          if (typeof visibleDetails.isFromUc === "string") {
            visibleDetails.isFromUc = visibleDetails.isFromUc === "true";
          }
          return visibleDetails;
        },
      ),
      comment: comment,
    };
    save(
      formData,
      () => console.log("success"),
      (error: any) => console.error({ error }),
      true,
    );
  };

  const updateAmountSum = (newAmountSum: number) => {
    amountSum = newAmountSum;
  }


  const infoConfig: ComponentConfig[] = RefinanceDetailsTab({
    setErrorMessages,
    updateAmountSum,
    internalRedemption,
    newRefinancingDetails,
    refinanceDetailsId,
    setNewRefinancingDetails,
    noErrorMessage,
    country,
  });

  const setColumnsForUserTask = () => {
    if (infoConfig.length > 2) {
      return 2;
    }
    if (infoConfig.length === 2) {
      return 1;
    }
  };


  const formConfig: ComponentConfig[] = LoanDetailsInfo({
    applicantInfo,
    loanDetails,
    setComment,
    errorMessages,
    comment,
    noErrorMessage,
  });

  return (
    <div>
      <CommonUserTaskNew
        handleComplete={onComplete}
        handleSave={onSave}
        infoConfig={infoConfig}
        formConfig={formConfig}
        overrideMasonryColumns={setColumnsForUserTask()}
        disablePrimary={errorMessages !== noErrorMessage}
      />
    </div>
  );
};

const UpdateRefinanceTask = (props: any) => (
  <QueryClientProvider client={queryClient}>
    <UpdateRefinance {...props} />
  </QueryClientProvider>
);

export default UpdateRefinanceTask;
